import { MihatoMark } from "@/common/components/MihatoMark";
import { ApiError } from "@/common/lib/api";
import { useAdminLogin } from "@/features/admin-auth/api/adminAuthApi";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAdminLogin();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    try {
      await login.mutateAsync({ email: email.trim().toLowerCase(), password });
      const redirect = searchParams.get("redirect") ?? "/admin/access-requests";
      navigate(redirect, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError("Demasiados intentos, esperá 15 minutos.");
          return;
        }
        if (err.status === 401) {
          setError("Email o contraseña incorrectos.");
          return;
        }
      }
      toast.error("No pudimos procesar tu pedido. Revisá tu conexión.");
    }
  }

  return (
    <>
      <title>Admin · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <main className="flex min-h-screen items-center justify-center bg-app-bg">
        <div className="w-full max-w-sm rounded-2xl border border-app-border bg-app-surface p-8 shadow-sm">
          <div className="mb-8 flex items-center gap-2.5">
            <MihatoMark className="h-6 w-6 text-app-ink-1" accent />
            <span className="font-medium text-app-ink-1 text-base tracking-tight">HATO</span>
          </div>

          <h1 className="mb-1 font-medium text-2xl text-app-ink-1 tracking-tight">Admin</h1>
          <p className="mb-6 text-app-ink-3 text-sm">Acceso restringido.</p>

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="admin-email" className="font-medium text-app-ink-2 text-sm">
                Email
              </label>
              <input
                id="admin-email"
                name="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@mihato.co"
                className={inputClass}
                disabled={login.isPending}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="admin-password" className="font-medium text-app-ink-2 text-sm">
                Contraseña
              </label>
              <input
                id="admin-password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                disabled={login.isPending}
              />
            </div>

            {error && (
              <p role="alert" className="text-rose-600 text-sm">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={login.isPending}
              className="mt-1 flex h-11 items-center justify-center rounded-xl bg-app-emerald px-5 font-medium text-sm text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {login.isPending ? "Ingresando…" : "Ingresar"}
            </button>
          </form>
        </div>
      </main>
    </>
  );
}

const inputClass =
  "h-11 w-full rounded-xl border border-app-border bg-app-bg px-4 text-base text-app-ink-1 placeholder:text-app-ink-3 focus:border-app-emerald focus:outline-none focus:ring-1 focus:ring-app-emerald/30 disabled:opacity-60";
